<script setup>
import { computed } from 'vue';
import BaseBubble from './Base.vue';
import FormattedContent from './Text/FormattedContent.vue';
import { useI18n } from 'vue-i18n';
import { CSAT_RATINGS, CSAT_DISPLAY_TYPES } from 'shared/constants/messages';
import { useMessageContext } from '../provider.js';

const { contentAttributes, content } = useMessageContext();
const { t } = useI18n();

const response = computed(() => {
  return contentAttributes.value?.submittedValues?.csatSurveyResponse ?? {};
});

const isRatingSubmitted = computed(() => {
  return !!response.value.rating;
});

const displayType = computed(() => {
  return contentAttributes.value?.displayType || CSAT_DISPLAY_TYPES.EMOJI;
});

const isStarRating = computed(() => {
  return displayType.value === CSAT_DISPLAY_TYPES.STAR;
});

const rating = computed(() => {
  if (isRatingSubmitted.value) {
    return CSAT_RATINGS.find(
      csatOption => csatOption.value === response.value.rating
    );
  }

  return null;
});

const starRatingValue = computed(() => {
  return response.value.rating || 0;
});
</script>

<template>
  <BaseBubble
    neutral-surface
    class="w-80 !max-w-full px-3 py-3"
    data-bubble-name="csat"
  >
    <FormattedContent v-if="content" :content="content" />
    <h4 v-else>{{ t('CONVERSATION.CSAT_REPLY_MESSAGE') }}</h4>
    <dl
      v-if="isRatingSubmitted"
      class="mt-3 rounded-lg border border-n-weak bg-n-solid-1 p-3 text-n-slate-12"
    >
      <dt class="text-xs font-medium text-n-slate-11">
        {{ t('CONVERSATION.RATING_TITLE') }}
      </dt>
      <dd v-if="!isStarRating && rating">
        {{ t(rating.translationKey) }}
      </dd>
      <dd
        v-else
        class="mt-1 flex items-center gap-1"
        :aria-label="`${starRatingValue}/5`"
      >
        <span
          v-for="n in 5"
          :key="n"
          aria-hidden="true"
          class="text-xl leading-none"
          :class="n <= starRatingValue ? 'text-[#b77900]' : 'text-n-slate-9'"
        >
          {{ n <= starRatingValue ? '★' : '☆' }}
        </span>
        <span class="ml-2 text-xs font-semibold text-n-slate-12">{{
          `${starRatingValue}/5`
        }}</span>
      </dd>

      <dt
        v-if="response.feedbackMessage"
        class="mt-3 border-t border-n-weak pt-2 text-xs font-medium text-n-slate-11"
      >
        {{ t('CONVERSATION.FEEDBACK_TITLE') }}
      </dt>
      <dd v-if="response.feedbackMessage" class="mt-1 break-words text-sm">
        {{ response.feedbackMessage }}
      </dd>
    </dl>
  </BaseBubble>
</template>
